import _regeneratorRuntime from "babel-runtime/regenerator";

function _asyncToGenerator(fn) { return function () { var gen = fn.apply(this, arguments); return new Promise(function (resolve, reject) { function step(key, arg) { try { var info = gen[key](arg); var value = info.value; } catch (error) { reject(error); return; } if (info.done) { resolve(value); } else { return Promise.resolve(value).then(function (value) { step("next", value); }, function (err) { step("throw", err); }); } } return step("next"); }); }; }

/** @jsx jsx */
import React, { useState } from "react";
import { observer } from "mobx-react";
import { get } from "lodash-es";
import { jsx } from "@emotion/core";
import { Alert, Box, Button, Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter, Input, useDisclosure, theme, ThemeProvider, CSSReset, Divider, Text, IconButton } from "@chakra-ui/core";
import { REQUEST_STATES } from "../core/common";
import { parseSheetUrl, useRequestStateHandler } from "../core/utils";
import SheetRangePicker from "./SheetRangePicker";

var rootStyles = {
  position: "fixed",
  left: 20,
  top: 100,
  width: 270
};

var FormDialog = observer(function (_ref) {
  var _this = this;

  var _ref$sheetUrl = _ref.sheetUrl,
      sheetUrl = _ref$sheetUrl === undefined ? "" : _ref$sheetUrl,
      isOpen = _ref.isOpen,
      _ref$handleSave = _ref.handleSave,
      handleSave = _ref$handleSave === undefined ? function () {} : _ref$handleSave,
      _ref$handleClose = _ref.handleClose,
      handleClose = _ref$handleClose === undefined ? function () {} : _ref$handleClose,
      store = _ref.store;

  var _useState = useState(sheetUrl),
      newSheetUrl = _useState[0],
      setSheetUrl = _useState[1];

  var _useState2 = useState(null),
      spreadsheet = _useState2[0],
      setSpreadsheet = _useState2[1];

  var _useRequestStateHandl = useRequestStateHandler(null),
      sheetRequestStatus = _useRequestStateHandl[0],
      setSheetRequestStatus = _useRequestStateHandl[1];

  var fetchSpreadsheet = function () {
    var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
      var _parseSheetUrl, spreadId, request, data;

      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              _parseSheetUrl = parseSheetUrl(newSheetUrl), spreadId = _parseSheetUrl.spreadId;

              setSheetRequestStatus(REQUEST_STATES.pending);
              _context.prev = 2;
              _context.next = 5;
              return fetch("https://sheets.googleapis.com/v4/spreadsheets/" + spreadId + "?includeGridData=true", {
                headers: {
                  "Content-Type": "application/json",
                  Authorization: "Bearer " + store.dataSourceProvider.authData.access_token
                }
              });

            case 5:
              request = _context.sent;
              _context.next = 8;
              return request.json();

            case 8:
              data = _context.sent;

              setSpreadsheet(data);
              _context.next = 15;
              break;

            case 12:
              _context.prev = 12;
              _context.t0 = _context["catch"](2);

              setSheetRequestStatus(REQUEST_STATES.rejected);

            case 15:

              setSheetRequestStatus(REQUEST_STATES.fulfilled);

            case 16:
            case "end":
              return _context.stop();
          }
        }
      }, _callee, _this, [[2, 12]]);
    }));

    return function fetchSpreadsheet() {
      return _ref2.apply(this, arguments);
    };
  }();
  var isPending = sheetRequestStatus === REQUEST_STATES.pending;
  var isFulfilled = sheetRequestStatus === REQUEST_STATES.fulfilled;
  var isRejected = sheetRequestStatus === REQUEST_STATES.rejected;

  return jsx(
    Modal,
    {
      isOpen: isOpen,
      onClose: handleClose,
      size: isFulfilled ? "70rem" : "md"
    },
    jsx(ModalOverlay, null),
    isFulfilled ? jsx(
      ModalContent,
      null,
      jsx(
        ModalHeader,
        null,
        "Select range with table"
      ),
      jsx(ModalCloseButton, null),
      jsx(
        ModalBody,
        null,
        jsx(SheetRangePicker, {
          spreadsheet: spreadsheet,
          handleClose: handleClose,
          handleSave: handleSave
        })
      )
    ) : jsx(
      ModalContent,
      null,
      jsx(
        ModalHeader,
        null,
        "New data source"
      ),
      jsx(ModalCloseButton, null),
      jsx(
        ModalBody,
        null,
        isRejected && jsx(
          Alert,
          { status: "error" },
          "Unexpected error occurred. Please check if you are authorized."
        ),
        jsx(Input, {
          label: "sheet url",
          value: newSheetUrl,
          onChange: function onChange(_ref3) {
            var target = _ref3.target;
            return setSheetUrl(target.value);
          },
          fullWidth: true,
          autoFocus: true
        })
      ),
      jsx(
        ModalFooter,
        null,
        jsx(
          Button,
          { onClick: handleClose, mr: 3 },
          "Cancel"
        ),
        jsx(
          Button,
          { onClick: fetchSpreadsheet, isLoading: isPending },
          "OK"
        )
      )
    )
  );
});

var CustomizedExpansionPanels = observer(function (_ref4) {
  var store = _ref4.store;
  var dataSourceProvider = store.dataSourceProvider;

  var _useDisclosure = useDisclosure(),
      isOpen = _useDisclosure.isOpen,
      onOpen = _useDisclosure.onOpen,
      onClose = _useDisclosure.onClose;

  var handleSave = function handleSave(accessData) {
    store.createAndAddDataSource({ accessData: accessData });
    onClose();
  };

  return jsx(
    Box,
    { p: 2 },
    dataSourceProvider ? jsx(
      Fragment,
      null,
      jsx(
        Box,
        { textAlign: "center", mb: 2 },
        "Google Sheets"
      ),
      jsx(
        Box,
        { w: "100%" },
        jsx(
          Box,
          { d: "flex", justifyContent: "center" },
          !dataSourceProvider.authData ? jsx(
            Button,
            {
              variant: "outlined",
              onClick: function onClick() {
                return dataSourceProvider.signIn();
              }
            },
            "Authorize"
          ) : jsx(
            Box,
            null,
            "Authorized:",
            " ",
            jsx(
              "strong",
              null,
              get(dataSourceProvider, "user.name")
            )
          )
        ),
        jsx(
          Box,
          { m: 3, d: "flex", justifyContent: "center" },
          jsx(
            Button,
            {
              bg: "transparent",
              border: "1px dashed #718096",
              onClick: onOpen,
              disabled: !dataSourceProvider.authData,
              w: "100%"
            },
            "New table"
          )
        ),
        jsx(
          Box,
          { mt: store.dataSources.length === 0 ? 0 : 2 },
          store.dataSources.map(function (d) {
            return jsx(
              Box,
              {
                key: d.id,
                p: 2,
                m: 3,
                bg: "gray.100",
                shadow: "md",
                borderRadius: "md"
              },
              !get(d, "data") ? "Loading..." : jsx(
                Box,
                { fontSize: "0.8rem" },
                jsx(
                  Box,
                  {
                    ml: 1,
                    display: "inline-flex",
                    alignItems: "center",
                    fontSize: "0.8rem",
                    w: "100%"
                  },
                  jsx(
                    Box,
                    { flexGrow: 1 },
                    "sheet name:",
                    " ",
                    jsx(
                      "strong",
                      null,
                      get(d, "accessData.spreadName")
                    )
                  ),
                  jsx(
                    Box,
                    null,
                    jsx(IconButton, {
                      icon: "arrow-left",
                      size: "sm",
                      onClick: function onClick() {
                        return d.decrementPivot();
                      },
                      disabled: d.pivot === 0
                    }),
                    d.pivot + 1,
                    jsx(IconButton, {
                      icon: "arrow-right",
                      size: "sm",
                      onClick: function onClick() {
                        return d.incrementPivot();
                      },
                      disabled: d.pivot === d.fields.length - 1
                    })
                  )
                ),
                jsx(
                  Box,
                  { mt: 1 },
                  jsx(
                    "strong",
                    null,
                    "Fields:"
                  ),
                  d.fields.map(function (_ref5, k) {
                    var label = _ref5.label,
                        value = _ref5.value;
                    return jsx(
                      Box,
                      { key: k, mt: 2 },
                      jsx(
                        Box,
                        { as: "span", fontWeight: 600 },
                        label,
                        ":"
                      ),
                      jsx(
                        Box,
                        { ml: 2, display: "inline", fontWeight: 400 },
                        value
                      )
                    );
                  })
                )
              )
            );
          })
        )
      )
    ) : jsx(
      Box,
      { textAlign: "center", mb: 2 },
      "No data source specified."
    ),
    jsx(FormDialog, {
      isOpen: isOpen,
      handleSave: handleSave,
      handleClose: onClose,
      store: store
    })
  );
});

function Panel(_ref6) {
  var store = _ref6.store,
      _ref6$rootStyle = _ref6.rootStyle,
      rootStyle = _ref6$rootStyle === undefined ? rootStyles : _ref6$rootStyle,
      rootClassName = _ref6.rootClassName;

  return jsx(
    ThemeProvider,
    { theme: theme },
    jsx(CSSReset, null),
    jsx(
      Box,
      {
        shadow: "lg",
        border: "1px",
        borderRadius: "md",
        borderColor: "gray.200",
        fontFamily: "avenir next, avenir, sans-serif",
        wordBreak: "break-all",
        fontSize: 14,
        lineHeight: "1.5",
        className: rootClassName,
        style: rootStyle
      },
      jsx(
        Box,
        { mt: 2, ml: 3 },
        jsx(
          Text,
          { as: "h2", fontWeight: 500 },
          "Data Sources"
        )
      ),
      jsx(Divider, null),
      jsx(CustomizedExpansionPanels, { store: store })
    )
  );
}

export default observer(Panel);